<template>
    <q-dialog v-model="model">
        <q-card class="desacopio-dialog">
            <!-- Header con gradiente -->
            <q-card-section class="card-header">
                <div class="row items-center no-wrap">
                    <q-icon name="mdi-basket-unfill" size="32px" class="q-mr-md" style="opacity: 0.95" />
                    <div class="col">
                        <div class="text-h5 text-weight-bold">Items Desacopiados</div>
                        <div class="text-caption text-grey-3">
                            {{ items.length }} {{ items.length === 1 ? 'item' : 'items' }} disponibles
                        </div>
                    </div>
                    <q-btn flat round dense icon="close" color="white" v-close-popup />
                </div>
            </q-card-section>

            <q-separator />

            <q-card-section class="table-container q-pa-none">
                <!-- Empty state -->
                <div v-if="items.length === 0" class="empty-state">
                    <q-icon name="mdi-basket-off-outline" size="64px" color="deep-purple-3" />
                    <div class="text-h6 text-grey-7 q-mt-md">No hay items desacopiados</div>
                    <div class="text-body2 text-grey-5 q-mt-xs">Seleccione items desde el presupuesto para desacopiar
                    </div>
                </div>

                <!-- Tabla -->
                <div v-else class="desacopio-table-wrapper">
                    <q-markup-table flat class="desacopio-table">
                        <thead>
                            <tr>
                                <th class="text-left">Producto</th>
                                <th class="text-right">Ancho</th>
                                <th class="text-right">Largo Pza</th>
                                <th class="text-right">Cantidad Pzas</th>
                                <th class="text-right">Cantidad</th>
                                <th class="text-right">Precio Unitario</th>
                                <th class="text-right">Descuento</th>
                                <th class="text-right">Total</th>
                                <th class="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(item, idx) in items" :key="item.id">
                                <!-- Fila padre -->
                                <tr class="parent-row"
                                    :class="{ 'has-children': getSubitemsByParent(item).length > 0, 'parent-separator': idx > 0 }">
                                    <td>
                                        <div class="row items-center no-wrap">
                                            <div class="product-info">
                                                <div class="product-code">{{ item.producto_id.trim() }}</div>
                                                <div class="product-name">{{ item.producto?.B1_DESC.trim() }}</div>
                                            </div>
                                            <q-badge v-if="getSubitemsByParent(item).length > 0" color="deep-purple-1"
                                                text-color="deep-purple-9" class="q-ml-sm children-badge">
                                                <q-icon name="account_tree" size="12px" class="q-mr-xs" />
                                                {{ getSubitemsByParent(item).length }}
                                            </q-badge>
                                        </div>
                                    </td>
                                    <td class="text-right text-weight-medium">{{ numberFormat(item.ancho, true) }} Mts
                                    </td>
                                    <td class="text-right text-weight-medium">{{ numberFormat(item.largo_pieza, true) }}
                                        Mts</td>
                                    <td class="text-right text-weight-medium"> {{ numberFormat(item.cantidad_piezas,
                                        true) }}
                                    </td>
                                    <td class="text-right text-weight-medium">
                                        {{ numberFormat(item.cantidad, true) }} {{ item.producto?.B1_UM }}
                                    </td>
                                    <td class="text-right price-cell">{{ numberFormat(item.precio_unitario) }}</td>
                                    <td class="text-right discount-cell">
                                        {{ item.descuento_comercial ? `${item.descuento_comercial}%` : '-' }}
                                    </td>
                                    <td class="text-right">
                                        <div class="total-breakdown">
                                            <span class="total-original">{{ numberFormat(item.total) }}</span>
                                            <template v-if="getSubitemsTotalByParent(item) > 0">
                                                <span class="total-subtract">
                                                    <q-icon name="remove" size="12px" /> {{
                                                        numberFormat(getSubitemsTotalByParent(item)) }}
                                                </span>
                                                <q-separator class="q-my-xs" />
                                                <span class="total-result">
                                                    = {{ numberFormat(item.total - getSubitemsTotalByParent(item)) }}
                                                </span>
                                            </template>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <q-btn unelevated dense icon="add" color="deep-purple" size="sm"
                                            @click="itemAdd(item)" class="action-btn">
                                            <q-tooltip>Agregar desacopio</q-tooltip>
                                        </q-btn>
                                    </td>
                                </tr>

                                <!-- Filas hijo (sub-items) -->
                                <tr v-for="sub in getSubitemsByParent(item)" :key="sub._uid" class="child-row">
                                    <td>
                                        <div class="row items-center no-wrap child-product">
                                            <q-icon name="subdirectory_arrow_right" size="18px" color="deep-purple-3"
                                                class="q-mr-sm" />
                                            <div class="product-info">
                                                <div class="product-code">{{ item.producto_id.trim() }}</div>
                                                <div class="product-name">{{ item.producto?.B1_DESC.trim() }}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-right text-grey-7">{{ numberFormat(sub.ancho,true) }}</td>
                                    <td class="text-right">
                                        <GltnInputNumber v-if="item.producto?.B1_WGRUP.trim() == 'WPAN'" dense v-model="sub.largo_pieza" :min="0" :step="0.01"
                                            suffix="Mts" @update:model-value="onItemUpdated(sub, item)" />
                                        <span v-else class="text-grey-7">{{ numberFormat(sub.largo_pieza,true) }} Mts</span>
                                    </td>
                                    <td class="text-right">
                                        <GltnInputNumber v-if="item.producto?.B1_WGRUP.trim() == 'WPAN'" dense v-model="sub.cantidad_piezas" :min="0" :step="0.01"
                                            @update:model-value="onItemUpdated(sub, item)" />
                                        <span v-else class="text-grey-7">{{ numberFormat(sub.cantidad_piezas,true) }}</span>
                                    </td>
                                    <td class="text-right">
                                        <GltnInputNumber v-if="item.producto?.B1_WGRUP.trim() !== 'WPAN'" dense v-model="sub.cantidad" :min="0" :step="0.01"
                                            @update:model-value="onItemUpdated(sub, item)" :suffix="item.producto?.B1_UM" />
                                        <span v-else class="text-grey-7">{{ numberFormat(sub.cantidad) }} {{ item.producto?.B1_UM }}</span>
                                    </td>
                                    <td class="text-right price-cell">{{ numberFormat(sub.precio_unitario) }}</td>
                                    <td class="text-right discount-cell">
                                        {{ sub.descuento_comercial ? `${sub.descuento_comercial}%` : '-' }}
                                    </td>
                                    <td class="text-right total-cell">{{ numberFormat(sub.total) }}</td>
                                    <td class="text-center">
                                        <q-btn unelevated dense icon="remove" color="red" size="sm"
                                            @click="itemRemove(item, sub)" class="action-btn">
                                            <q-tooltip>Eliminar desacopio</q-tooltip>
                                        </q-btn>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </q-markup-table>
                </div>
            </q-card-section>
            <q-card-actions class="q-pa-md actions-fixed">
                <div class="row full-width q-gutter-sm">
                    <div class="col">
                        <q-btn outline label="Cancelar" color="grey-7" icon="close" v-close-popup class="full-width" />
                    </div>
                    <div class="col">
                        <q-btn :disabled="confirmarDisabled" unelevated label="Confirmar" color="primary" icon="check" class="full-width"
                            @click="onConfirm" />
                    </div>
                </div>
            </q-card-actions>
            <q-inner-loading :showing="loading">
                <q-spinner-hourglass size="50px" color="primary" />
            </q-inner-loading>
        </q-card>
    </q-dialog>
</template>

<script setup>
import numberFormat from "@/helpers/numberFormat";
import GltnInputNumber from "@/components/form/GltnInputNumber.vue";
import PresupuestoService from "@/services/PresupuestoService";
import { useQuasar } from "quasar";
import { round } from "mathjs";
import { ref, computed } from "vue";

const $q = useQuasar();
const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
    presupId: {
        type: [String, Number],
        required: true,
    },
});

const emits = defineEmits(['confirm']);
const confirmarDisabled = ref(true);
const loading = ref(false);
const itemsNew = ref([]);
const model = ref(false);

const getGrupoByParent = (parent) => {
    return itemsNew.value.find((group) => group.parentId === parent.id);
};

const getSubitemsByParent = (parent) => {
    return getGrupoByParent(parent)?.subitem ?? [];
};

const getSubitemsTotalByParent = (parent) => {
    const subitems = getSubitemsByParent(parent);
    return subitems.reduce((acc, sub) => acc + (sub.total || 0), 0);
};

const getSubitemsCantidadByParent = (parent) => {
    const subitems = getSubitemsByParent(parent);
    return subitems.reduce((acc, sub) => parseFloat(acc) + (parseFloat(sub.cantidad) || 0), 0);
};

const itemAdd = (item) => {
    const codigo = item.producto?.B1_COD || item.producto_id;
    const grupo = getGrupoByParent(item);
    const nuevoSubitem = {
        ...item,
        total: 0,
        largo_pieza: 0,
        cantidad_piezas: 0,
        cantidad: 0,
        _uid: crypto.randomUUID()
    };

    if (grupo) {
        grupo.subitem.push(nuevoSubitem);
    } else {
        itemsNew.value.push({
            parentId: item.id,
            producto: {
                B1_COD: codigo,
                B1_DESC: item.producto?.B1_DESC || "",
                B1_UM: item.producto?.B1_UM || "",
            },
            subitem: [nuevoSubitem],
        });
    }
};

const itemRemove = (parent, subitem) => {
    const grupo = getGrupoByParent(parent);

    if (!grupo) {
        return;
    }

    grupo.subitem = grupo.subitem.filter((s) => s._uid !== subitem._uid);

    if (grupo.subitem.length === 0) {
        itemsNew.value = itemsNew.value.filter(
            (g) => g.parentId !== parent.id,
        );
    }
};

const precioConDescuento = (item) => {
    let precio = round(item.precio_unitario, 2);
    const descuentoComercial = round(item.descuento_comercial || 0, 2);
    const descuentoEstrategico = round(item.descuento_estrategico || 0, 2);
    if (descuentoComercial) {
        precio -= round((precio / 100) * descuentoComercial, 2);
    }
    if (descuentoEstrategico) {
        precio -= round((precio / 100) * descuentoEstrategico, 2);
    }
    return precio;
};

const onItemUpdated = (item, parent) => {
    if(item.producto?.B1_WGRUP.trim() === 'WPAN') {
        item.cantidad = parseFloat(item.ancho) * parseFloat(item.largo_pieza) * parseFloat(item.cantidad_piezas);
    }
    item.total = round(item.cantidad * precioConDescuento(item), 2);
    const test = getSubitemsCantidadByParent(parent);
    if (getSubitemsCantidadByParent(parent) > parseFloat(parent.cantidad)) {
        item.cantidad = 0;
        item.largo_pieza = 0;
        item.cantidad_piezas = 0;
        item.total = 0;
        $q.notify({
            message: "La cantidad del desacopio no puede ser mayor a la cantidad del item padre",
            icon: "warning",
            color: "negative",
        });
        confirmarDisabled.value = true;
        return;
    }
    confirmarDisabled.value = false;
};

const onConfirm = async () => {
    const desacopiados = itemsNew.value.map((grupo) => {
        return grupo.subitem.map((sub) => ({
            item_id: sub.id,
            largo_pieza: sub.largo_pieza,
            cantidad_piezas: sub.cantidad_piezas,
            cantidad: sub.cantidad,
            total: sub.total,
        }));
    }).flat();
    loading.value = true;
    try {
        await PresupuestoService.desacopio(props.presupId, desacopiados);
        emits('success');
    } catch (error) {
        console.error("Error al desacopiar items:", error);
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.desacopio-dialog {
    min-width: 700px;
    max-width: 95vw;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

.card-header {
    background: linear-gradient(135deg, #5e35b1 0%, #4527a0 100%);
    color: white;
    padding: 24px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 32px;
}

.table-container {
    background: #fafafa;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.desacopio-table-wrapper {

    // Scrollbar personalizado
    ::-webkit-scrollbar {
        width: 8px;
    }

    ::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
            background: #555;
        }
    }
}

.desacopio-table {
    :deep(thead tr th) {
        background: linear-gradient(to bottom, #f5f5f5 0%, #eeeeee 100%);
        color: #424242;
        font-weight: 600;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 14px 12px;
        border-bottom: 2px solid #e0e0e0;
    }

    :deep(tbody) {
        background: white;
    }

    // Filas padre
    .parent-row {
        border-left: 4px solid #5e35b1;
        transition: all 0.2s ease;

        td {
            padding: 14px 12px;
            border-bottom: 1px solid #f0f0f0;
            font-size: 14px;
        }

        &:hover {
            background: linear-gradient(to right, #ede7f6 0%, #f8f9fa 100%);
        }

        // Padre que tiene hijos: reforzar visualmente
        &.has-children {
            td {
                background: rgba(94, 53, 177, 0.03);
                font-weight: 600;
                border-bottom-color: transparent;
            }
        }

        // Separador entre grupos (a partir del 2do padre)
        &.parent-separator {
            td {
                border-top: 3px solid #e0e0e0;
            }
        }
    }

    // Filas hijo (sub-items)
    .child-row {
        border-left: 4px solid #ce93d8;
        background: #f3e5f5;
        transition: all 0.2s ease;

        td {
            padding: 10px 12px;
            border-bottom: 1px solid #e1bee7;
            font-size: 13px;
        }

        &:hover {
            background: linear-gradient(to right, #e1bee7 0%, #f3e5f5 100%);
        }

        .child-product {
            padding-left: 8px;
        }
    }

    // Info de producto
    .product-info {
        display: flex;
        flex-direction: column;

        .product-code {
            font-size: 12px;
            font-weight: 600;
            color: #5e35b1;
            margin-bottom: 2px;
        }

        .product-name {
            font-size: 13px;
            color: #424242;
            line-height: 1.3;
        }
    }

    .children-badge {
        font-size: 11px;
        font-weight: 600;
        border-radius: 10px;
        padding: 2px 8px;
    }

    // Celdas de precio y total
    .price-cell {
        color: #1976d2;
        font-weight: 600;
        font-size: 14px;
    }

    .discount-cell {
        color: #e65100;
        font-weight: 600;
        font-size: 13px;
    }

    .total-cell {
        color: #388e3c;
        font-weight: 600;
        font-size: 14px;
        background: linear-gradient(to right, transparent 0%, #e8f5e9 100%);
    }

    .total-breakdown {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
        min-width: 90px;

        .total-original {
            color: #388e3c;
            font-weight: 600;
            font-size: 14px;
        }

        .total-subtract {
            color: #d32f2f;
            font-weight: 500;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 2px;
        }

        .total-result {
            color: #1565c0;
            font-weight: 700;
            font-size: 14px;
            background: linear-gradient(to right, transparent 0%, #e3f2fd 100%);
            padding: 2px 6px;
            border-radius: 4px;
        }
    }

    // Botones de acción
    .action-btn {
        border-radius: 6px;
        transition: all 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }
    }
}

// Responsive
@media (max-width: 1024px) {
    .desacopio-dialog {
        min-width: 90vw;
    }

    .card-header {
        padding: 16px;

        .text-h5 {
            font-size: 18px;
        }
    }

    .desacopio-table {

        .parent-row td,
        .child-row td {
            padding: 10px 8px;
            font-size: 12px;
        }

        .price-cell,
        .total-cell {
            font-size: 12px;
        }
    }
}

@media (max-width: 600px) {
    .desacopio-dialog {
        min-width: 100vw;
        border-radius: 0;
        max-height: 100vh;
    }

    .desacopio-table {
        :deep(thead tr th) {
            font-size: 11px;
            padding: 10px 6px;
        }

        .parent-row td,
        .child-row td {
            padding: 8px 6px;
            font-size: 11px;
        }
    }
}
</style>
